import { Component } from "@theme/component";
import { convertMoneyToMinorUnits } from "@theme/money-formatting";
import { sectionRenderer } from "@theme/section-renderer";

/**
 * Sends filter analytics through the theme analytics wrapper.
 * @param {"filters:click_filters" | "filters:remove_filters"} eventName - The filter event name
 * @param {string | undefined | null} filterName - The Shopify filter label
 * @param {string | undefined | null} filterValue - The selected or removed value
 * @param {"bar" | "filtro"} tracker - The UI area that triggered the event
 */
function pushFilterAnalyticsEvent(eventName, filterName, filterValue, tracker) {
  const normalizedFilterName = filterName?.trim();
  const normalizedFilterValue = filterValue?.trim();
  const analytics = window.CustomShopify?.Analytics;

  if (!normalizedFilterName || !normalizedFilterValue || !analytics?.pushEvent) {
    return;
  }

  analytics
    .pushEvent({
      event: eventName,
      filter_name: normalizedFilterName,
      filter_value: normalizedFilterValue,
      tracker,
    })
    .catch(() => {});
}

/**
 * Formats a min-max interval for analytics.
 * @param {string} minLabel - Minimum label
 * @param {string} maxLabel - Maximum label
 * @returns {string}
 */
function formatFilterInterval(minLabel, maxLabel) {
  return minLabel === maxLabel ? minLabel : `${minLabel} - ${maxLabel}`;
}

/**
 * Custom dual-handle price facet used only when the custom filter setting is enabled.
 *
 * @typedef {Object} CustomPriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement | undefined} rangeMin - The minimum price range input
 * @property {HTMLInputElement | undefined} rangeMax - The maximum price range input
 * @property {HTMLElement | undefined} rangeTrack - The highlighted range track
 *
 * @extends {Component<CustomPriceFacetRefs>}
 */
class CustomPriceFacetComponent extends Component {
  /** @type {string} */
  currency;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keydown", this.#onKeyDown);
    this.currency = this.dataset.currency ?? "USD";
    this.#setRangeStep();
    this.#syncRangeUIFromTextInputs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this.#onKeyDown);
  }

  /**
   * Updates the slider state while the user drags a range handle
   * @param {InputEvent} event - The range input event
   */
  updateRangeInputs(event) {
    const target = event.target;
    const { rangeMin, rangeMax, minInput, maxInput } = this.refs;

    if (
      !(target instanceof HTMLInputElement) ||
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement)
    ) {
      return;
    }

    const minValue = this.#parseRangeValue(rangeMin.value);
    const maxValue = this.#parseRangeValue(rangeMax.value);
    const minGap = this.#getRangeMinGap();

    if (target === rangeMin && minValue > maxValue - minGap) {
      rangeMin.value = Math.max(
        maxValue - minGap,
        this.#getRangeMin(),
      ).toString();
    }

    if (target === rangeMax && maxValue < minValue + minGap) {
      rangeMax.value = Math.min(
        minValue + minGap,
        this.#getRangeMax(),
      ).toString();
    }

    const nextMin = this.#parseRangeValue(rangeMin.value);
    const nextMax = this.#parseRangeValue(rangeMax.value);

    minInput.value = nextMin > this.#getRangeMin() ? this.#formatRangeValue(nextMin) : "";
    maxInput.value =
      nextMax < this.#getRangeMax() ? this.#formatRangeValue(nextMax) : "";

    this.#updateRangeTrack();
    this.#updateSummary();
  }

  /**
   * Keeps manually typed price values to whole currency units
   * @param {InputEvent} event - The price input event
   */
  sanitizeWholePriceInput(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.target.value = this.#sanitizeWholeCurrencyValue(event.target.value);
    this.#syncRangeUIFromTextInputs();
    this.#updateSummary();
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#normalizeBoundaryInputs();
    this.#adjustToMinimumGap();
    this.#syncRangeUIFromTextInputs();
    this.#pushRangeAnalytics();

    const facetsForm = /** @type {HTMLElement & { updateFilters?: () => void }} */ (
      this.closest("facets-form-component")
    );
    if (!(facetsForm instanceof HTMLElement) || !facetsForm.updateFilters) {
      return;
    }

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Handles keydown events to restrict manual inputs to whole numbers
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey) return;

    const pattern =
      /[0-9]|Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Parses a formatted money value into minor units
   * @param {string} displayValue - The display value
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Parses a range input value stored in minor units
   * @param {string} value - The raw slider value
   * @returns {number} The slider value in minor units
   */
  #parseRangeValue(value) {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  /**
   * Sanitizes a typed price value to whole currency units
   * @param {string} value - The user-entered value
   * @returns {string} The integer-only value
   */
  #sanitizeWholeCurrencyValue(value) {
    const trimmedValue = value.trim();
    if (!trimmedValue) return "";

    const decimalMatch = trimmedValue.match(/^(.+)[,.](\d{1,2})$/);
    const integerPart = decimalMatch?.[1] ?? trimmedValue;

    return integerPart.replace(/\D/g, "");
  }

  /**
   * Formats a slider value as whole currency units for Shopify price params
   * @param {number} value - The range value in minor units
   * @returns {string} The value in major units without decimal cents
   */
  #formatRangeValue(value) {
    return Math.round(value / this.#getRangeStep()).toString();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === "") return;

    const value = this.#parseDisplayValue(input.value, this.currency);
    const min = this.#parseDisplayValue(
      input.getAttribute("data-min") ?? "0",
      this.currency,
    );
    const max = this.#parseDisplayValue(
      input.getAttribute("data-max") ?? "0",
      this.currency,
    );

    if (value < min) {
      input.value = this.#formatRangeValue(min);
    } else if (value > max) {
      input.value = this.#formatRangeValue(max);
    }
  }

  /**
   * Keeps manually entered min/max values at least the configured gap apart
   */
  #adjustToMinimumGap() {
    const { minInput, maxInput } = this.refs;

    if (!minInput.value || !maxInput.value) return;

    const minValue = this.#parseDisplayValue(minInput.value, this.currency);
    const maxValue = this.#parseDisplayValue(maxInput.value, this.currency);
    const minGap = this.#getRangeMinGap();

    if (minGap <= 0 || maxValue - minValue >= minGap) return;

    const rangeMin = this.#getRangeMin();
    const rangeMax = this.#getRangeMax();
    const nextMax = Math.min(minValue + minGap, rangeMax);

    if (nextMax - minValue >= minGap) {
      maxInput.value = this.#formatRangeValue(nextMax);
      return;
    }

    minInput.value = this.#formatRangeValue(Math.max(nextMax - minGap, rangeMin));
    maxInput.value = this.#formatRangeValue(nextMax);
  }

  /**
   * Treats boundary values as empty so manual input matches slider behavior.
   */
  #normalizeBoundaryInputs() {
    const { minInput, maxInput } = this.refs;
    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) {
      return;
    }

    if (minInput.value) {
      const minValue = this.#parseDisplayValue(minInput.value, this.currency);
      if (minValue <= this.#getRangeMin()) {
        minInput.value = "";
      }
    }

    if (maxInput.value) {
      const maxValue = this.#parseDisplayValue(maxInput.value, this.currency);
      if (maxValue >= this.#getRangeMax()) {
        maxInput.value = "";
      }
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") {
      maxInput.setAttribute(
        "data-min",
        this.#formatRangeValue(this.#getRangeMin()),
      );
    }
    if (maxInput.value === "")
      minInput.setAttribute(
        "data-max",
        maxInput.getAttribute("data-max") ?? "",
      );
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest("details");
    const statusComponent =
      /** @type {HTMLElement & { updatePriceSummary?: (minInput: HTMLInputElement, maxInput: HTMLInputElement) => void } | null | undefined} */ (
        details?.querySelector("facet-status-component")
      );

    if (
      !(statusComponent instanceof HTMLElement) ||
      !statusComponent.updatePriceSummary
    ) {
      return;
    }

    statusComponent.updatePriceSummary(minInput, maxInput);
  }

  /**
   * Pushes click/remove analytics for the current price range.
   */
  #pushRangeAnalytics() {
    const isFullRangeSelected = this.#isFullRangeSelected();

    pushFilterAnalyticsEvent(
      isFullRangeSelected ? "filters:remove_filters" : "filters:click_filters",
      this.dataset.filterName,
      this.#getAnalyticsFilterValue(),
      isFullRangeSelected ? "filtro" : "bar",
    );
  }

  /**
   * Checks if the current price range represents the full available range.
   * @returns {boolean}
   */
  #isFullRangeSelected() {
    const { minInput, maxInput } = this.refs;
    return !minInput.value && !maxInput.value;
  }

  /**
   * Gets the selected price range as a display interval.
   * @returns {string}
   */
  #getAnalyticsFilterValue() {
    const { minInput, maxInput } = this.refs;
    const symbol = this.dataset.currencySymbol ?? "";
    const minValue = minInput.value || this.#formatRangeValue(this.#getRangeMin());
    const maxValue = maxInput.value || this.#formatRangeValue(this.#getRangeMax());

    return formatFilterInterval(`${symbol}${minValue}`, `${symbol}${maxValue}`);
  }

  /**
   * Synchronizes the range handles from the text inputs
   */
  #syncRangeUIFromTextInputs() {
    const { rangeMin, rangeMax, minInput, maxInput } = this.refs;

    if (
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement)
    ) {
      return;
    }

    const minValue = minInput.value
      ? this.#parseDisplayValue(minInput.value, this.currency)
      : this.#getRangeMin();
    const maxValue = maxInput.value
      ? this.#parseDisplayValue(maxInput.value, this.currency)
      : this.#getRangeMax();

    rangeMin.value = Math.min(minValue, maxValue).toString();
    rangeMax.value = Math.max(maxValue, minValue).toString();
    this.#updateRangeTrack();
  }

  /**
   * Returns the slider minimum in minor units
   * @returns {number}
   */
  #getRangeMin() {
    return this.#parseRangeValue(this.dataset.rangeMin ?? "0");
  }

  /**
   * Returns the slider maximum in minor units
   * @returns {number}
   */
  #getRangeMax() {
    return this.#parseRangeValue(this.dataset.rangeMax ?? "0");
  }

  /**
   * Returns the slider step in minor units
   * @returns {number}
   */
  #getRangeStep() {
    return Math.max(this.#parseRangeValue(this.dataset.rangeStep ?? "100"), 1);
  }

  /**
   * Returns the minimum distance between range handles in minor units
   * @returns {number}
   */
  #getRangeMinGap() {
    const configuredGap = Math.max(
      this.#parseRangeValue(this.dataset.rangeMinGap ?? "0"),
      0,
    );
    const rangeSpan = Math.max(this.#getRangeMax() - this.#getRangeMin(), 0);

    return Math.min(configuredGap, rangeSpan);
  }

  /**
   * Applies the whole-unit step to range handles
   */
  #setRangeStep() {
    const { rangeMin, rangeMax } = this.refs;
    const rangeStep = this.#getRangeStep().toString();

    if (rangeMin instanceof HTMLInputElement) rangeMin.step = rangeStep;
    if (rangeMax instanceof HTMLInputElement) rangeMax.step = rangeStep;
  }

  /**
   * Updates the filled track between the two range handles
   */
  #updateRangeTrack() {
    const { rangeMin, rangeMax, rangeTrack } = this.refs;

    if (
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement) ||
      !(rangeTrack instanceof HTMLElement)
    ) {
      return;
    }

    const sliderMin = this.#getRangeMin();
    const sliderMax = this.#getRangeMax();
    const denominator = Math.max(sliderMax - sliderMin, 1);
    const start =
      ((this.#parseRangeValue(rangeMin.value) - sliderMin) / denominator) * 100;
    const end =
      ((this.#parseRangeValue(rangeMax.value) - sliderMin) / denominator) * 100;

    rangeTrack.style.left = `${start}%`;
    rangeTrack.style.right = `${100 - end}%`;
    this.#updateOverlapState(
      this.#parseRangeValue(rangeMin.value),
      this.#parseRangeValue(rangeMax.value),
    );
  }

  /**
   * Keeps the movable handle reachable when both handles overlap at an edge.
   * @param {number} minValue - The current minimum handle value
   * @param {number} maxValue - The current maximum handle value
   */
  #updateOverlapState(minValue, maxValue) {
    const isOverlapping = minValue === maxValue;
    this.classList.toggle(
      "custom-price-facet--overlap-at-max",
      isOverlapping && maxValue >= this.#getRangeMax(),
    );
    this.classList.toggle(
      "custom-price-facet--overlap-at-min",
      isOverlapping && minValue <= this.#getRangeMin(),
    );
  }
}

if (!customElements.get("custom-price-facet-component")) {
  customElements.define(
    "custom-price-facet-component",
    CustomPriceFacetComponent,
  );
}

/**
 * Custom generic range facet used for Shopify list filters ending in " - Range".
 *
 * @typedef {Object} CustomRangeFacetRefs
 * @property {HTMLInputElement[]} facetInputs - Hidden checkbox inputs for each list value
 * @property {HTMLInputElement | undefined} rangeMin - The minimum range input
 * @property {HTMLInputElement | undefined} rangeMax - The maximum range input
 * @property {HTMLElement | undefined} rangeTrack - The highlighted range track
 *
 * @extends {Component<CustomRangeFacetRefs>}
 */
class CustomRangeFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sortNumericValuesWhenPossible();
    this.#syncRangeFromActiveInputs();
    this.#updateRangeTrack();
    this.#updateSummary();
  }

  /**
   * Updates the visible slider state while the user drags a range handle.
   * @param {InputEvent} event - The range input event
   */
  updateRangeInputs(event) {
    const target = event.target;
    const { rangeMin, rangeMax } = this.refs;

    if (
      !(target instanceof HTMLInputElement) ||
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement)
    ) {
      return;
    }

    const minValue = this.#parseRangeValue(rangeMin.value);
    const maxValue = this.#parseRangeValue(rangeMax.value);

    if (target === rangeMin && minValue > maxValue) {
      rangeMin.value = maxValue.toString();
    }

    if (target === rangeMax && maxValue < minValue) {
      rangeMax.value = minValue.toString();
    }

    this.#updateRangeTrack();
    this.#updateSummary();
  }

  /**
   * Checks the hidden list inputs in the selected range and refreshes results.
   */
  updateRangeFilterAndResults() {
    this.#applyCheckboxSelection();
    this.#pushRangeAnalytics();

    const facetsForm = /** @type {HTMLElement & { updateFilters?: () => void }} */ (
      this.closest("facets-form-component")
    );
    if (!(facetsForm instanceof HTMLElement) || !facetsForm.updateFilters) {
      return;
    }

    facetsForm.updateFilters();
    this.#updateSummary();
  }

  /**
   * Parses a range input value as an integer index.
   * @param {string} value - The raw range value
   * @returns {number}
   */
  #parseRangeValue(value) {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  /**
   * Returns the slider minimum index.
   * @returns {number}
   */
  #getRangeMin() {
    return this.#parseRangeValue(this.dataset.rangeMin ?? "0");
  }

  /**
   * Returns the slider maximum index.
   * @returns {number}
   */
  #getRangeMax() {
    return this.#parseRangeValue(this.dataset.rangeMax ?? "0");
  }

  /**
   * Reads a checkbox's serialized list index.
   * @param {HTMLInputElement} input - The checkbox input
   * @returns {number}
   */
  #getInputIndex(input) {
    return this.#parseRangeValue(input.dataset.index ?? "0");
  }

  /**
   * Parses labels with embedded numbers, such as "0,2 ct" or "12 mm".
   * @param {string} label - The rendered list value label
   * @returns {number}
   */
  #parseNumericLabel(label) {
    const normalizedLabel = label.toLowerCase().replace(",", ".");
    const match = normalizedLabel.match(/\d+(?:\.\d+)?/);
    if (!match) return Number.NaN;

    const parsedValue = Number.parseFloat(match[0]);
    return Number.isFinite(parsedValue) ? parsedValue : Number.NaN;
  }

  /**
   * Numeric filters keep their natural numeric order even if Shopify returns
   * values alphabetically; nonnumeric filters keep the merchant-provided order.
   */
  #sortNumericValuesWhenPossible() {
    const { facetInputs } = this.refs;
    if (!Array.isArray(facetInputs) || facetInputs.length === 0) return;

    const valuesWithNumbers = facetInputs.map((input) => ({
      input,
      value: this.#parseNumericLabel(input.dataset.label ?? ""),
    }));

    if (valuesWithNumbers.some((item) => Number.isNaN(item.value))) return;

    valuesWithNumbers
      .sort((firstItem, secondItem) => firstItem.value - secondItem.value)
      .forEach((item, index) => {
        item.input.dataset.index = index.toString();
      });

    const markers = Array.from(
      this.querySelectorAll(".custom-range-facet__marker"),
    );
    const markersWithNumbers = markers.map((marker) => ({
      marker,
      value: this.#parseNumericLabel(marker.textContent ?? ""),
    }));

    if (markersWithNumbers.some((item) => Number.isNaN(item.value))) return;

    const denominator = Math.max(markersWithNumbers.length - 1, 1);

    markersWithNumbers
      .sort((firstItem, secondItem) => firstItem.value - secondItem.value)
      .forEach((item, index) => {
        if (!(item.marker instanceof HTMLElement)) return;
        item.marker.style.left = `${(index / denominator) * 100}%`;
        item.marker.style.order = index.toString();
        item.marker.classList.toggle(
          "custom-range-facet__marker--above",
          index % 2 === 0,
        );
        item.marker.classList.toggle(
          "custom-range-facet__marker--below",
          index % 2 !== 0,
        );
      });
  }

  /**
   * Finds the selected range from already active Shopify list values.
   */
  #syncRangeFromActiveInputs() {
    const { facetInputs, rangeMin, rangeMax } = this.refs;

    if (
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement) ||
      !Array.isArray(facetInputs)
    ) {
      return;
    }

    const activeInputs = facetInputs.filter((input) => input.checked);
    if (activeInputs.length === 0) {
      rangeMin.value = this.#getRangeMin().toString();
      rangeMax.value = this.#getRangeMax().toString();
      return;
    }

    const activeIndexes = activeInputs.map((input) => this.#getInputIndex(input));
    rangeMin.value = Math.min(...activeIndexes).toString();
    rangeMax.value = Math.max(...activeIndexes).toString();
  }

  /**
   * Selects every hidden checkbox in the slider range, except when the full
   * range is selected; in that case no list params are needed.
   */
  #applyCheckboxSelection() {
    const { facetInputs, rangeMin, rangeMax } = this.refs;

    if (
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement) ||
      !Array.isArray(facetInputs)
    ) {
      return;
    }

    const selectedMin = this.#parseRangeValue(rangeMin.value);
    const selectedMax = this.#parseRangeValue(rangeMax.value);
    const isFullRange =
      selectedMin <= this.#getRangeMin() && selectedMax >= this.#getRangeMax();

    facetInputs.forEach((input) => {
      const inputIndex = this.#getInputIndex(input);
      input.checked =
        !isFullRange && inputIndex >= selectedMin && inputIndex <= selectedMax;
    });
  }

  /**
   * Updates the text shown beside the facet label.
   */
  #updateSummary() {
    const { rangeMin, rangeMax, facetInputs } = this.refs;

    if (
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement) ||
      !Array.isArray(facetInputs)
    ) {
      return;
    }

    const status = this.closest("details")?.querySelector(
      'facet-status-component [ref="facetStatus"]',
    );
    if (!(status instanceof HTMLElement)) return;

    const selectedMin = this.#parseRangeValue(rangeMin.value);
    const selectedMax = this.#parseRangeValue(rangeMax.value);
    const isFullRange =
      selectedMin <= this.#getRangeMin() && selectedMax >= this.#getRangeMax();

    if (isFullRange) {
      status.textContent = "";
      return;
    }

    const minLabel = this.#getLabelForIndex(selectedMin);
    const maxLabel = this.#getLabelForIndex(selectedMax);

    status.textContent = formatFilterInterval(minLabel, maxLabel);
  }

  /**
   * Pushes click/remove analytics for the current generic range.
   */
  #pushRangeAnalytics() {
    const isFullRangeSelected = this.#isFullRangeSelected();

    pushFilterAnalyticsEvent(
      isFullRangeSelected ? "filters:remove_filters" : "filters:click_filters",
      this.dataset.filterName,
      this.#getAnalyticsFilterValue(),
      isFullRangeSelected ? "filtro" : "bar",
    );
  }

  /**
   * Checks if the current slider range covers every list value.
   * @returns {boolean}
   */
  #isFullRangeSelected() {
    const { rangeMin, rangeMax } = this.refs;

    if (
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement)
    ) {
      return false;
    }

    return (
      this.#parseRangeValue(rangeMin.value) <= this.#getRangeMin() &&
      this.#parseRangeValue(rangeMax.value) >= this.#getRangeMax()
    );
  }

  /**
   * Gets the selected range as a display interval.
   * @returns {string}
   */
  #getAnalyticsFilterValue() {
    const { rangeMin, rangeMax } = this.refs;

    if (
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement)
    ) {
      return "";
    }

    return formatFilterInterval(
      this.#getLabelForIndex(this.#parseRangeValue(rangeMin.value)),
      this.#getLabelForIndex(this.#parseRangeValue(rangeMax.value)),
    );
  }

  /**
   * Gets the rendered label for a list value index.
   * @param {number} index - The list value index
   * @returns {string}
   */
  #getLabelForIndex(index) {
    const { facetInputs } = this.refs;
    if (!Array.isArray(facetInputs)) return "";

    const input = facetInputs.find(
      (facetInput) => this.#getInputIndex(facetInput) === index,
    );

    return input?.dataset.label ?? "";
  }

  /**
   * Updates the filled track between the two range handles.
   */
  #updateRangeTrack() {
    const { rangeMin, rangeMax, rangeTrack } = this.refs;

    if (
      !(rangeMin instanceof HTMLInputElement) ||
      !(rangeMax instanceof HTMLInputElement) ||
      !(rangeTrack instanceof HTMLElement)
    ) {
      return;
    }

    const sliderMin = this.#getRangeMin();
    const sliderMax = this.#getRangeMax();
    const denominator = Math.max(sliderMax - sliderMin, 1);
    const start =
      ((this.#parseRangeValue(rangeMin.value) - sliderMin) / denominator) * 100;
    const end =
      ((this.#parseRangeValue(rangeMax.value) - sliderMin) / denominator) * 100;

    rangeTrack.style.setProperty(
      "--custom-range-facet-range-start",
      `${start}%`,
    );
    rangeTrack.style.setProperty("--custom-range-facet-range-end", `${end}%`);
    this.#updateOverlapState(
      this.#parseRangeValue(rangeMin.value),
      this.#parseRangeValue(rangeMax.value),
    );
  }

  /**
   * Keeps the movable handle reachable when both handles overlap at an edge.
   * @param {number} minValue - The current minimum handle value
   * @param {number} maxValue - The current maximum handle value
   */
  #updateOverlapState(minValue, maxValue) {
    const isOverlapping = minValue === maxValue;
    this.classList.toggle(
      "custom-range-facet--overlap-at-max",
      isOverlapping && maxValue >= this.#getRangeMax(),
    );
    this.classList.toggle(
      "custom-range-facet--overlap-at-min",
      isOverlapping && minValue <= this.#getRangeMin(),
    );
  }
}

if (!customElements.get("custom-range-facet-component")) {
  customElements.define("custom-range-facet-component", CustomRangeFacetComponent);
}

/**
 * Collection tag fallback facet used when Shopify storefront filters are not returned for oversized collections.
 *
 * @typedef {Object} CustomTagFiltersRefs
 * @property {HTMLScriptElement | undefined} data - Serialized Liquid tag data
 * @property {HTMLTemplateElement | undefined} caretIcon - Caret icon template
 * @property {HTMLTemplateElement | undefined} closeIcon - Remove icon template
 * @property {HTMLElement | undefined} activeFilters - Active filter pill container
 * @property {HTMLElement | undefined} filters - Filter accordion container
 *
 * @typedef {Object} ParsedTagFilter
 * @property {string} tag - Original Shopify tag
 * @property {string} name - Filter group name
 * @property {string} displayName - Visible filter group name
 * @property {number} filterOrder - Display order for the filter group
 * @property {number} order - Numeric order inside the filter
 * @property {string} label - Visible option label
 *
 * @typedef {Object} CustomTagFilterAvailabilityEntry
 * @property {string} collectionPath - Collection path the entry belongs to
 * @property {string[]} selectedTagHandles - Canonical selected tag handles
 * @property {string[]} availableTags - Filter tags available for this exact combination
 * @property {string[]} excludedTags - Filter tags unavailable for this exact combination
 *
 * @extends {Component<CustomTagFiltersRefs>}
 */
class CustomTagFiltersComponent extends Component {
  /** @type {ParsedTagFilter[]} */
  #options = [];

  /** @type {Set<string>} */
  #availableTags = new Set();

  /** @type {Set<string>} */
  #currentTags = new Set();

  /** @type {Set<string>} */
  #excludedTags = new Set();

  /** @type {Map<string, Promise<boolean>>} */
  #availabilityRequests = new Map();

  /** @type {number} */
  #availabilityRequestId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#loadData();
    this.#render();
    this.#updateAvailability();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#loadData();
    this.#render();
    this.#updateAvailability();
  }

  /**
   * Refreshes refs and redraws the generated filter UI after a section morph.
   */
  refresh() {
    super.updatedCallback();
    this.#loadData();
    this.#render();
    this.#updateAvailability();
  }

  /**
   * Removes all active fallback tag filters.
   * @param {Event} event - The triggering event
   */
  clearFilters(event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
    }

    const nextTags = this.#getNonFilterCurrentTags();
    this.#pushClearAllAnalytics();
    this.#navigateToTags(nextTags);
  }

  /**
   * Reads and parses the serialized Liquid data.
   */
  #loadData() {
    const dataElement = this.refs.data;
    if (!(dataElement instanceof HTMLScriptElement)) return;

    /** @type {{ allTags?: string[], availableTags?: string[], currentTags?: string[] }} */
    let data = {};
    try {
      data = JSON.parse(dataElement.textContent ?? "{}");
    } catch {
      data = {};
    }

    const allTags = this.#getPersistedAllTags(
      Array.isArray(data.allTags) ? data.allTags : [],
    );
    const currentTags = Array.isArray(data.currentTags) ? data.currentTags : [];
    const liquidAvailableTags = Array.isArray(data.availableTags)
      ? data.availableTags
      : [];
    const availableTags =
      currentTags.length === 0
        ? allTags
        : [...new Set([...liquidAvailableTags, ...currentTags])];

    this.#options = allTags
      .map((tag) => this.#parseFilterTag(tag))
      .filter((option) => option !== null);

    const previousCurrentTags = this.#currentTags;
    this.#availableTags = new Set(availableTags);
    this.#currentTags = new Set(currentTags);
    this.#syncExcludedTags(previousCurrentTags);
    this.#applyCachedAvailability();
  }

  /**
   * Keeps unavailable candidate tags only while active tag filters are being narrowed.
   * @param {Set<string>} previousCurrentTags - Active tags before the latest Liquid data load
   */
  #syncExcludedTags(previousCurrentTags) {
    if (
      this.#currentTags.size === 0 ||
      previousCurrentTags.size === 0 ||
      !this.#isSuperset(this.#currentTags, previousCurrentTags)
    ) {
      this.#excludedTags.clear();
    }

    for (const tag of this.#currentTags) {
      this.#excludedTags.delete(tag);
    }
  }

  /**
   * Checks if every tag from the second set is present in the first set.
   * @param {Set<string>} tags - Candidate superset
   * @param {Set<string>} subset - Required subset
   * @returns {boolean}
   */
  #isSuperset(tags, subset) {
    for (const tag of subset) {
      if (!tags.has(tag)) return false;
    }

    return true;
  }

  /**
   * Adds exact cached availability and derived cached exclusions for the current tag set.
   */
  #applyCachedAvailability() {
    if (this.#currentTags.size === 0) return;

    const cache = this.#availabilityCache;
    const currentKey = this.#buildCombinationKey([...this.#currentTags]);
    const currentHandles = new Set(this.#getCanonicalTagHandles([...this.#currentTags]));
    const exactEntry = cache.combinations.get(currentKey);

    if (exactEntry) {
      for (const tag of exactEntry.availableTags) this.#availableTags.add(tag);
      for (const tag of exactEntry.excludedTags) this.#excludedTags.add(tag);
    }

    cache.combinations.forEach((entry, key) => {
      if (key === currentKey || entry.collectionPath !== this.#collectionCachePath) return;
      if (!this.#isHandleSubset(entry.selectedTagHandles, currentHandles)) return;

      for (const tag of entry.excludedTags) {
        this.#excludedTags.add(tag);
      }
    });

    for (const tag of this.#currentTags) {
      this.#excludedTags.delete(tag);
    }
  }

  /**
   * Stores the current exact availability in window memory until the page reloads.
   */
  #saveCachedAvailability() {
    if (this.#currentTags.size === 0) return;

    const cache = this.#availabilityCache;
    cache.combinations.set(this.#buildCombinationKey([...this.#currentTags]), {
      collectionPath: this.#collectionCachePath,
      selectedTagHandles: this.#getCanonicalTagHandles([...this.#currentTags]),
      availableTags: [...this.#availableTags].filter((tag) => this.#parseFilterTag(tag)),
      excludedTags: [...this.#excludedTags].filter((tag) => this.#parseFilterTag(tag)),
    });
  }

  /**
   * Checks whether all handles in the first list are present in the second set.
   * @param {string[]} handles - Candidate subset handles
   * @param {Set<string>} availableHandles - Available handles
   * @returns {boolean}
   */
  #isHandleSubset(handles, availableHandles) {
    for (const handle of handles) {
      if (!availableHandles.has(handle)) return false;
    }

    return true;
  }

  /**
   * Keeps the complete fallback filter set available after navigating to filtered tag URLs.
   * @param {string[]} allTags - Tags from Liquid for the current render
   * @returns {string[]}
   */
  #getPersistedAllTags(allTags) {
    const storageKey = this.#storageKey;
    const filterTags = allTags.filter((tag) => this.#parseFilterTag(tag));
    let persistedTags = /** @type {string[]} */ ([]);

    try {
      const storedValue = window.sessionStorage.getItem(storageKey);
      const parsedValue = storedValue ? JSON.parse(storedValue) : [];
      persistedTags = Array.isArray(parsedValue)
        ? parsedValue.filter((tag) => this.#parseFilterTag(tag))
        : [];
    } catch {
      persistedTags = [];
    }

    const mergedTags = [...new Set([...persistedTags, ...filterTags])];

    if (mergedTags.length > 0) {
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(mergedTags));
      } catch {}
    }

    return mergedTags.length > filterTags.length ? mergedTags : allTags;
  }

  /**
   * Parses a project filter tag.
   * @param {string} tag - Original Shopify tag
   * @returns {ParsedTagFilter | null}
   */
  #parseFilterTag(tag) {
    if (typeof tag !== "string" || !tag.startsWith("filter__")) return null;

    const parts = tag.split("__");
    if (parts.length < 4) return null;

    const name = parts[1]?.trim() ?? "";
    const order = Number.parseFloat(parts[2] ?? "");
    const label = parts.slice(3).join("__").trim();

    if (!name || !label || !Number.isFinite(order)) return null;

    const filterConfig = this.#getFilterConfig(name);
    const displayName = filterConfig.label ?? name;
    const filterOrder = filterConfig.order ?? 999999;

    return { tag, name, displayName, filterOrder, order, label };
  }

  /**
   * Renders active chips and grouped filter accordions.
   */
  #render() {
    this.#renderActiveFilters();
    this.#renderFilterGroups();
    this.toggleAttribute("hidden", this.#options.length === 0);
  }

  /**
   * Updates availability with fetch checks because collection.tags is unreliable on tagged collection pages.
   */
  async #updateAvailability() {
    const requestId = ++this.#availabilityRequestId;

    if (this.#currentTags.size === 0) {
      this.#excludedTags.clear();
      this.#availableTags = new Set(this.#options.map((option) => option.tag));
      this.#render();
      return;
    }

    const availableTags = new Set(this.#availableTags);

    const optionsToCheck = this.#options.filter(
      (option) =>
        !this.#currentTags.has(option.tag) &&
        !this.#excludedTags.has(option.tag) &&
        !this.#availableTags.has(option.tag) &&
        !this.#isExclusiveBlocked(option),
    );

    await this.#runAvailabilityChecks(optionsToCheck, async (option) => {
      const isAvailable = await this.#fetchOptionAvailability(option);

      if (requestId !== this.#availabilityRequestId) return;

      if (isAvailable) availableTags.add(option.tag);
      else this.#excludedTags.add(option.tag);
    });

    if (requestId !== this.#availabilityRequestId) return;

    this.#availableTags = availableTags;
    this.#saveCachedAvailability();
    this.#render();
  }

  /**
   * Runs availability checks with low concurrency to reduce Shopify 429 responses.
   * @param {ParsedTagFilter[]} options - Options missing from collection.tags
   * @param {(option: ParsedTagFilter) => Promise<void>} callback - Availability check callback
   */
  async #runAvailabilityChecks(options, callback) {
    const pendingOptions = [...options];
    const workerCount = Math.min(this.#availabilityConcurrency, pendingOptions.length);

    await Promise.all(
      Array.from({ length: workerCount }, async () => {
        while (pendingOptions.length > 0) {
          const option = pendingOptions.shift();
          if (!option) return;

          await callback(option);
        }
      }),
    );
  }

  /**
   * Maximum number of parallel availability check requests.
   * @returns {number}
   */
  get #availabilityConcurrency() {
    const concurrency = Number.parseInt(this.dataset.availabilityConcurrency ?? "3", 10);
    if (!Number.isFinite(concurrency)) return 3;

    return Math.min(Math.max(concurrency, 1), 6);
  }

  /**
   * Renders active fallback tag pills.
   */
  #renderActiveFilters() {
    const container = this.refs.activeFilters;
    if (!(container instanceof HTMLElement)) return;

    container.replaceChildren();

    const activeOptions = this.#options.filter((option) =>
      this.#currentTags.has(option.tag),
    );

    if (activeOptions.length === 0) return;

    const heading = document.createElement("h3");
    heading.className = "visually-hidden";
    heading.textContent = this.dataset.activeFiltersLabel ?? "Active filters:";
    container.append(heading);

    activeOptions
      .sort(this.#sortOptions)
      .forEach((option) => {
        const pill = document.createElement("facet-remove-component");
        pill.className = "pills__pill pills__pill--desktop-small facets-remove__pill";
        pill.dataset.filterName = option.displayName;
        pill.dataset.filterValue = option.label;
        pill.tabIndex = 0;
        pill.setAttribute("role", "button");
        pill.append(document.createTextNode(this.#getActivePillLabel(option)));
        this.#appendCloseIcon(pill);
        pill.addEventListener("click", () => this.#toggleOption(option));
        pill.addEventListener("keydown", (event) => this.#handleActivePillKeydown(event, option));
        container.append(pill);
      });
  }

  /**
   * Renders all filter groups as Vessel facet accordions.
   */
  #renderFilterGroups() {
    const container = this.refs.filters;
    if (!(container instanceof HTMLElement)) return;

    container.replaceChildren();

    for (const [name, options] of this.#getGroupedOptions()) {
      container.append(this.#createAccordion(name, options));
    }
  }

  /**
   * Groups options by filter name.
   * @returns {Map<string, ParsedTagFilter[]>}
   */
  #getGroupedOptions() {
    const groupedOptions = new Map();

    this.#options.sort(this.#sortOptions).forEach((option) => {
      const existingOptions = groupedOptions.get(option.name) ?? [];
      existingOptions.push(option);
      groupedOptions.set(option.name, existingOptions);
    });

    return groupedOptions;
  }

  /**
   * Creates a filter accordion.
   * @param {string} name - Filter group name
   * @param {ParsedTagFilter[]} options - Filter options
   * @returns {HTMLElement}
   */
  #createAccordion(name, options) {
    const displayName = options[0]?.displayName ?? name;
    const accordion = document.createElement("accordion-custom");
    accordion.className = `facets__item ${this.#getFilterClass(name)}`;
    accordion.setAttribute("open", "");
    accordion.setAttribute("open-by-default-on-mobile", "");
    accordion.setAttribute("open-by-default-on-desktop", "");
    accordion.dataset.filterParamName = this.#getFilterClass(name);

    const details = document.createElement("details");
    details.className = "facets__panel";
    if (this.dataset.filterStyle === "vertical") details.open = true;

    details.append(this.#createSummary(displayName, options));
    details.append(this.#createPanel(name, options));
    accordion.append(details);

    return accordion;
  }

  /**
   * Creates the facet summary row.
   * @param {string} name - Filter group name
   * @param {ParsedTagFilter[]} options - Filter options
   * @returns {HTMLElement}
   */
  #createSummary(name, options) {
    const summary = document.createElement("summary");
    summary.className = "facets__summary";

    const labelWrapper = document.createElement("span");
    labelWrapper.className = "facets__label-wrapper";

    const label = document.createElement("span");
    label.className = "facets__label";
    label.textContent = name;
    labelWrapper.append(label);

    const statusWrapper = document.createElement("div");
    statusWrapper.className = "facets__status-wrapper";

    const status = document.createElement("facet-status-component");
    status.className = "facets__status";
    status.setAttribute("facet-type", "list");
    status.dataset.filterStyle = this.dataset.filterStyle ?? "vertical";

    const statusText = document.createElement("span");
    statusText.className = this.#getStatusClass(options);
    statusText.setAttribute("ref", "facetStatus");
    statusText.textContent = this.#getStatusText(options);
    status.append(statusText);

    statusWrapper.append(status);
    this.#appendCaretIcon(statusWrapper);

    summary.append(labelWrapper, statusWrapper);
    return summary;
  }

  /**
   * Creates the filter options panel.
   * @param {string} name - Filter group name
   * @param {ParsedTagFilter[]} options - Filter options
   * @returns {HTMLElement}
   */
  #createPanel(name, options) {
    const panel = document.createElement("floating-panel-component");
    panel.className = "facets__inputs facets__panel-content details-content";

    const inputsWrapper = document.createElement("div");
    inputsWrapper.className = "facets__inputs-wrapper facets__inputs-wrapper--row";

    const list = document.createElement("ul");
    list.className =
      "facets__inputs-list facets__inputs-list--grid facets__inputs-list--forced-pills list-unstyled";
    list.setAttribute("name", name);

    options.forEach((option, index) => {
      list.append(this.#createOptionItem(option, index));
    });

    inputsWrapper.append(list);
    panel.append(inputsWrapper);
    return panel;
  }

  /**
   * Creates a single button-style option.
   * @param {ParsedTagFilter} option - Filter option
   * @param {number} index - Option index inside the filter
   * @returns {HTMLElement}
   */
  #createOptionItem(option, index) {
    const item = document.createElement("li");
    const isActive = this.#currentTags.has(option.tag);
    const isDisabled = !isActive && !this.#isOptionAvailable(option);
    item.className = `facets__inputs-list-item${isDisabled ? " facets__inputs-list-item--disabled" : ""}`;

    const wrapper = document.createElement("div");
    wrapper.className = "facets__pill-wrapper";
    wrapper.addEventListener("keydown", (event) => this.#handleOptionKeydown(event, option));

    const input = document.createElement("input");
    input.type = "checkbox";
    input.id = `${this.id}-${this.#handleize(option.name)}-${index}`;
    input.className = "facets__pill-input";
    input.dataset.label = option.label;
    input.checked = isActive;
    input.disabled = isDisabled;
    input.tabIndex = -1;
    input.addEventListener("change", () => this.#toggleOption(option));

    const label = document.createElement("label");
    label.className = "facets__pill-label";
    label.htmlFor = input.id;
    label.tabIndex = isDisabled ? -1 : 0;
    label.textContent = option.label;

    if (isDisabled) {
      label.append(this.#createDisabledLine());
    }

    wrapper.append(input, label);
    item.append(wrapper);
    return item;
  }

  /**
   * Toggles a filter option and navigates to the next tag URL.
   * @param {ParsedTagFilter} option - Filter option
   */
  #toggleOption(option) {
    const nextTags = new Set(this.#currentTags);
    const isActive = nextTags.has(option.tag);

    if (isActive) {
      nextTags.delete(option.tag);
    } else {
      nextTags.add(option.tag);
    }

    pushFilterAnalyticsEvent(
      isActive ? "filters:remove_filters" : "filters:click_filters",
      option.displayName,
      option.label,
      isActive ? "filtro" : "bar",
    );

    this.#navigateToTags([...nextTags]);
  }

  /**
   * Navigates to a collection tag URL, using section rendering when possible.
   * @param {string[]} tags - Next selected tags
   */
  async #navigateToTags(tags) {
    const url = this.#buildTagUrl(tags);

    try {
      history.pushState("", "", url);
      document.dispatchEvent(new CustomEvent("custom:tag-filter-update", {
        detail: { hasActiveFilters: tags.length > 0 },
      }));
      await sectionRenderer.renderSection(this.#sectionId, {
        cache: false,
        url: new URL(window.location.href),
      });
      this.#refreshRenderedComponents();
    } catch {
      window.location.href = url.toString();
    }
  }

  /**
   * Refreshes live fallback components because the theme morph callback runs on the parsed fragment.
   */
  #refreshRenderedComponents() {
    const section = document.getElementById(`shopify-section-${this.#sectionId}`);
    const components = section?.querySelectorAll("custom-tag-filters-component") ?? [];

    components.forEach((component) => {
      if (component instanceof CustomTagFiltersComponent) {
        component.refresh();
      }
    });
  }

  /**
   * Builds the Shopify collection tag URL.
   * @param {string[]} tags - Selected raw tag labels
   * @returns {URL}
   */
  #buildTagUrl(tags) {
    const collectionUrl = this.dataset.collectionUrl ?? window.location.pathname;
    const url = new URL(collectionUrl, window.location.origin);
    const tagHandles = this.#getCanonicalTagHandles(tags);

    if (tagHandles.length > 0) {
      url.pathname = `${url.pathname.replace(/\/$/, "")}/${tagHandles.join("+")}`;
    }

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.forEach((value, key) => {
      if (key !== "page" && key !== "section_id") url.searchParams.append(key, value);
    });

    return url;
  }

  /**
   * Returns order-insensitive canonical tag handles.
   * @param {string[]} tags - Raw tag labels
   * @returns {string[]}
   */
  #getCanonicalTagHandles(tags) {
    return [...new Set(tags.map((tag) => this.#handleize(tag)).filter(Boolean))].sort(
      (firstHandle, secondHandle) => firstHandle.localeCompare(secondHandle),
    );
  }

  /**
   * Builds an order-insensitive cache key scoped to the current collection path.
   * @param {string[]} tags - Raw selected tags
   * @returns {string}
   */
  #buildCombinationKey(tags) {
    return `${this.#collectionCachePath}::${this.#getCanonicalTagHandles(tags).join("+")}`;
  }

  /**
   * Handles keyboard activation for pill labels.
   * @param {KeyboardEvent} event - Keydown event
   * @param {ParsedTagFilter} option - Filter option
   */
  #handleOptionKeydown(event, option) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();

    if (!this.#currentTags.has(option.tag) && !this.#isOptionAvailable(option)) {
      return;
    }

    this.#toggleOption(option);
  }

  /**
   * Checks if an inactive option can currently be selected.
   * @param {ParsedTagFilter} option - Filter option
   * @returns {boolean}
   */
  #isOptionAvailable(option) {
    if (this.#currentTags.has(option.tag)) return true;
    if (this.#excludedTags.has(option.tag)) return false;
    if (this.#isExclusiveBlocked(option)) return false;

    return this.#availableTags.has(option.tag);
  }

  /**
   * Checks if an option is blocked by another active value in the same exclusive filter group.
   * @param {ParsedTagFilter} option - Filter option
   * @returns {boolean}
   */
  #isExclusiveBlocked(option) {
    if (!this.#isExclusiveFilter(option.name)) return false;

    return this.#options.some(
      (candidate) =>
        candidate.name === option.name &&
        candidate.tag !== option.tag &&
        this.#currentTags.has(candidate.tag),
    );
  }

  /**
   * All fallback filter groups are exclusive except scelta_rapida.
   * @param {string} name - Raw filter group name
   * @returns {boolean}
   */
  #isExclusiveFilter(name) {
    return name.toLowerCase().replace(/[\s_-]+/g, "") !== "sceltarapida";
  }

  /**
   * Fetches a lightweight collection URL to check if adding an option would return products.
   * @param {ParsedTagFilter} option - Filter option
   * @returns {Promise<boolean>}
   */
  #fetchOptionAvailability(option) {
    const url = this.#buildAvailabilityUrl(option);
    const requestKey = url.toString();
    const pendingRequest = this.#availabilityRequests.get(requestKey);

    if (pendingRequest) return pendingRequest;

    const request = fetch(url, {
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) return true;
        return response.text().then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, "text/html");
          const indication = documentFragment.querySelector("#tagProductsCount[product-count]");
          if (!indication) return true;

          const productCount = Number(indication.getAttribute("product-count"));
          return Number.isFinite(productCount) && productCount > 0;
        });
      })
      .catch(() => true);

    this.#availabilityRequests.set(requestKey, request);

    return request.finally(() => {
      this.#availabilityRequests.delete(requestKey);
    });
  }

  /**
   * Builds the URL used only for availability checks.
   * @param {ParsedTagFilter} option - Candidate option
   * @returns {URL}
   */
  #buildAvailabilityUrl(option) {
    const tags = [...new Set([...this.#currentTags, option.tag])];
    const url = this.#buildTagUrl(tags);
    const template = this.dataset.availabilityTemplate?.trim();

    if (template) {
      url.searchParams.set("view", template);
    }

    return url;
  }

  /**
   * Handles keyboard activation for active remove pills.
   * @param {KeyboardEvent} event - Keydown event
   * @param {ParsedTagFilter} option - Active option
   */
  #handleActivePillKeydown(event, option) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    this.#toggleOption(option);
  }

  /**
   * Returns current non-fallback tags so clear all preserves manual collection tag context.
   * @returns {string[]}
   */
  #getNonFilterCurrentTags() {
    return [...this.#currentTags].filter((tag) => !this.#parseFilterTag(tag));
  }

  /**
   * Pushes analytics for clearing all fallback filter tags.
   */
  #pushClearAllAnalytics() {
    this.#options
      .filter((option) => this.#currentTags.has(option.tag))
      .forEach((option) => {
        pushFilterAnalyticsEvent(
          "filters:remove_filters",
          option.displayName,
          option.label,
          "filtro",
        );
      });
  }

  /**
   * Appends the shared caret icon template.
   * @param {Element} parent - Parent element
   */
  #appendCaretIcon(parent) {
    const template = this.refs.caretIcon;
    if (!(template instanceof HTMLTemplateElement)) return;

    parent.append(template.content.cloneNode(true));
  }

  /**
   * Appends the shared close icon template.
   * @param {Element} parent - Parent element
   */
  #appendCloseIcon(parent) {
    const template = this.refs.closeIcon;
    if (!(template instanceof HTMLTemplateElement)) return;

    parent.append(template.content.cloneNode(true));
  }

  /**
   * Creates the disabled diagonal line used by button-style filters.
   * @returns {SVGElement}
   */
  #createDisabledLine() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", "100%");
    svg.setAttribute("viewBox", "0 0 100 100");
    svg.setAttribute("preserveAspectRatio", "none");

    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("x1", "100");
    line.setAttribute("y1", "0");
    line.setAttribute("x2", "0");
    line.setAttribute("y2", "100");
    line.setAttribute("vector-effect", "non-scaling-stroke");
    svg.append(line);

    return svg;
  }

  /**
   * Gets active status text for a filter group.
   * @param {ParsedTagFilter[]} options - Group options
   * @returns {string}
   */
  #getStatusText(options) {
    const activeOptions = options.filter((option) => this.#currentTags.has(option.tag));
    if (activeOptions.length === 0) return "";

    if (this.dataset.filterStyle === "horizontal" && activeOptions.length === 1) {
      return activeOptions[0]?.label ?? "";
    }

    return activeOptions.length.toString();
  }

  /**
   * Gets the CSS classes for a group status bubble.
   * @param {ParsedTagFilter[]} options - Group options
   * @returns {string}
   */
  #getStatusClass(options) {
    const activeCount = options.filter((option) => this.#currentTags.has(option.tag)).length;
    const classes = ["hide-when-empty"];

    if (
      activeCount > 0 &&
      (this.dataset.filterStyle !== "horizontal" || activeCount > 1)
    ) {
      classes.push("bubble", "facets__bubble");
    }

    return classes.join(" ");
  }

  /**
   * Gets the active pill label.
   * @param {ParsedTagFilter} option - Filter option
   * @returns {string}
   */
  #getActivePillLabel(option) {
    return option.label;
  }

  /**
   * Matches the theme's filter class normalization closely enough for custom hide rules.
   * @param {string} name - Filter label
   * @returns {string}
   */
  #getFilterClass(name) {
    return `filter_${name
      .trim()
      .toLowerCase()
      .replace(/\s+-\s+/g, "_")
      .replace(/[\s\-&/.,:;()]+/g, "_")
      .replace(/^_+|_+$/g, "")}`;
  }

  /**
   * Reads optional display configuration for a fallback filter group.
   * @param {string} name - Raw filter name from the tag
   * @returns {{ label?: string, order?: number }}
   */
  #getFilterConfig(name) {
    const config = window.CustomTagFilterFallbackConfig;
    const filters = config && typeof config === "object" ? config.filters : undefined;
    if (!filters || typeof filters !== "object") return {};

    const normalizedName = name.trim().toLowerCase();
    const candidates = [
      name,
      normalizedName,
      this.#handleize(name),
      this.#getFilterClass(name).replace(/^filter_/, ""),
    ];
    const filterConfig = candidates
      .map((key) => filters[key])
      .find((value) => value && typeof value === "object");

    if (!filterConfig) return {};

    const label =
      typeof filterConfig.label === "string" && filterConfig.label.trim()
        ? filterConfig.label.trim()
        : undefined;
    const order = Number(filterConfig.order);

    return {
      label,
      order: Number.isFinite(order) ? order : undefined,
    };
  }

  /**
   * Creates a Shopify-style tag handle for collection tag URLs.
   * @param {string} value - Raw tag label
   * @returns {string}
   */
  #handleize(value) {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /**
   * Sorts by filter name, numeric order, then label.
   * @param {ParsedTagFilter} firstOption - First option
   * @param {ParsedTagFilter} secondOption - Second option
   * @returns {number}
   */
  #sortOptions(firstOption, secondOption) {
    return (
      firstOption.filterOrder - secondOption.filterOrder ||
      firstOption.displayName.localeCompare(secondOption.displayName, undefined, { sensitivity: "base" }) ||
      firstOption.order - secondOption.order ||
      firstOption.label.localeCompare(secondOption.label, undefined, { sensitivity: "base" })
    );
  }

  /**
   * Section ID used by Shopify section rendering.
   * @returns {string}
   */
  get #sectionId() {
    return this.dataset.sectionId ?? "";
  }

  /**
   * Shared in-memory availability cache, reset on full page reload.
   * @returns {CustomTagFilterFallbackAvailabilityCache}
   */
  get #availabilityCache() {
    if (window.CustomTagFilterFallbackAvailability?.combinations instanceof Map) {
      return window.CustomTagFilterFallbackAvailability;
    }

    const cache = { combinations: new Map() };
    window.CustomTagFilterFallbackAvailability = cache;
    return cache;
  }

  /**
   * Collection path used to scope in-memory availability cache entries.
   * @returns {string}
   */
  get #collectionCachePath() {
    const collectionUrl = this.dataset.collectionUrl ?? window.location.pathname;
    return new URL(collectionUrl, window.location.origin).pathname.replace(/\/$/, "");
  }

  /**
   * Storage key for the root collection fallback filters.
   * @returns {string}
   */
  get #storageKey() {
    return `custom-tag-filters:${this.dataset.collectionUrl ?? window.location.pathname}`;
  }
}

if (!customElements.get("custom-tag-filters-component")) {
  customElements.define("custom-tag-filters-component", CustomTagFiltersComponent);
}
